import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  RefreshCw, Check, ChevronDown, ChevronUp, Award, Target, BarChart3,
  Info, ArrowLeft, AlertTriangle, FileText, Download, Loader2, Sun, Moon, Layers
} from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/App";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SHADE_COLORS = {A1:"#F5E6C8",A2:"#EEDCB8",A3:"#E5CFA0","A3.5":"#DDC490",A4:"#D4B87C",B1:"#F2E8D0",B2:"#E8DABA",B3:"#DCCCA4",B4:"#D0BD8E",C1:"#EDE6D4",C2:"#E0D8BE",C3:"#D4CBA8",C4:"#C7BC92",D2:"#EBE2CC",D3:"#DDD4B6",D4:"#CFC6A0"};
const CS = {
  Imperceptible:{color:"text-emerald-400",bg:"bg-emerald-400/10",border:"border-emerald-400/20",score:100},
  Excellent:{color:"text-emerald-400",bg:"bg-emerald-400/10",border:"border-emerald-400/20",score:90},
  Good:{color:"text-sky-400",bg:"bg-sky-400/10",border:"border-sky-400/20",score:70},
  Acceptable:{color:"text-amber-400",bg:"bg-amber-400/10",border:"border-amber-400/20",score:50},
  Approximate:{color:"text-orange-400",bg:"bg-orange-400/10",border:"border-orange-400/20",score:30},
};
const WI = { glare:Sun, shadow:Moon, uneven_lighting:AlertTriangle };

export default function ResultsScreen({ result, image, onStartNew }) {
  const navigate = useNavigate();
  const [labSlipOpen, setLabSlipOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showCalData, setShowCalData] = useState(false);
  const [labForm, setLabForm] = useState({ email:"", name:"", patient_id:"", clinician_name:"", clinic_name:"", notes:"" });

  useEffect(() => { trackEvent("page_view", "results"); if (!result) navigate("/"); }, [result, navigate]);
  if (!result) return null;

  const { mode, primary_match, secondary_match, corrected_tooth_lab, calibration_applied, all_matches, warnings, zones } = result;
  const ps = CS[primary_match.confidence] || CS.Approximate;

  const handleStartNew = () => { onStartNew(); navigate("/"); };

  const handleExportPDF = async () => {
    if (!labForm.email) { toast.error("Email is required to export the lab slip"); return; }
    if (!labForm.name) { toast.error("Name is required to export the lab slip"); return; }
    setExporting(true);
    try {
      const payload = {
        ...labForm, mode,
        primary_shade:primary_match.shade, primary_delta_e:primary_match.delta_e, primary_confidence:primary_match.confidence,
        secondary_shade:secondary_match.shade, secondary_delta_e:secondary_match.delta_e,
        calibration_applied, corrected_lab:corrected_tooth_lab,
        warnings:warnings||[],
        zones:zones?zones.map(z=>({zone:z.zone,primary_shade:z.primary_match.shade,primary_delta_e:z.primary_match.delta_e,primary_confidence:z.primary_match.confidence})):null,
      };
      const response = await axios.post(`${API}/generate-lab-slip`, payload, { responseType:"blob" });
      const url = window.URL.createObjectURL(new Blob([response.data],{type:"application/pdf"}));
      const link = document.createElement("a"); link.href=url;
      link.setAttribute("download",`SmileSense-lab-slip-${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      setLabSlipOpen(false);
      toast.success("Lab slip downloaded!");
    } catch (err) { console.error(err); toast.error("Failed to generate lab slip"); }
    finally { setExporting(false); }
  };

  const MC = (match, label, icon, iconColor, isHero) => {
    const s = CS[match.confidence]||CS.Approximate; const I=icon;
    return (
      <Card data-testid={`${label.toLowerCase().replace(/\s/g,'-')}-card`} className="rounded-2xl bg-[#232134] border-[#33304a] overflow-hidden">
        {isHero && <div className="h-2 w-full" style={{background:"linear-gradient(90deg,#ff9f1c,#e91e63,#2196f3)"}}/>}
        <CardContent className={isHero?"p-5":"p-4"}>
          <div className="flex items-center gap-2 mb-3"><I className={`w-4 h-4 ${iconColor}`}/><span className={`text-xs font-medium uppercase tracking-widest ${iconColor}`}>{label}</span></div>
          <div className="flex items-center gap-4">
            <div data-testid={`${label.toLowerCase().replace(/\s/g,'-')}-swatch`}
              className={`${isHero?"w-20 h-20":"w-14 h-14"} rounded-xl border-2 border-white/10 shadow-inner flex items-center justify-center`}
              style={{backgroundColor:SHADE_COLORS[match.shade]||"#ccc"}}>
              <span className={`${isHero?"text-2xl":"text-lg"} font-bold text-[#1a182a] drop-shadow-sm`}>{match.shade}</span>
            </div>
            <div className="flex-1 space-y-2">
              <div><div className={`${isHero?"text-3xl":"text-xl"} font-bold text-white tracking-tight`}>{match.shade}</div>
                <div className="text-xs text-[#7a7894]">{match.description} &middot; Group {match.group}</div></div>
              <div className="flex items-center gap-2">
                <Badge className={`text-xs rounded-full px-3 ${s.bg} ${s.color} border ${s.border}`}>{match.confidence}</Badge>
                <Tooltip><TooltipTrigger><span className="text-xs text-[#5a5873]">DE {match.delta_e}</span></TooltipTrigger>
                  <TooltipContent className="bg-[#1a182a] border-[#33304a] text-white"><p>Delta E 2000: {match.delta_e}</p></TooltipContent></Tooltip>
              </div>
              {isHero && <Progress value={s.score} className="h-1.5 bg-[#2a273b]"/>}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <TooltipProvider>
      <div data-testid="results-screen" className="min-h-screen flex flex-col pb-8 max-w-lg mx-auto">
        <div className="flex items-center gap-3 px-5 py-4">
          <Button data-testid="back-to-analysis-btn" variant="ghost" size="icon" onClick={() => navigate("/analyze")}
            className="rounded-xl text-[#7a7894] hover:text-white hover:bg-[#2a273b]"><ArrowLeft className="w-5 h-5"/></Button>
          <div className="flex-1"><h2 className="text-lg font-semibold text-white">Match Results</h2>
            <p className="text-xs text-[#5a5873]">Step 3 of 3 &mdash; {mode==="multipoint"?"Multi-point shade map":"Shade recommendation"}</p></div>
          {mode==="multipoint" && <Badge className="bg-[#e91e63]/10 text-[#e91e63] border border-[#e91e63]/20 text-xs rounded-full"><Layers className="w-3 h-3 mr-1"/> 3 Zones</Badge>}
        </div>

        {/* Warnings */}
        {warnings?.length > 0 && (
          <div className="px-5 mb-4 space-y-2">{warnings.map((w,i)=>{const W=WI[w.type]||AlertTriangle;return(
            <div key={i} data-testid={`warning-${w.type}-${i}`} className={`flex items-start gap-3 p-3 rounded-xl border ${w.severity==="high"?"bg-red-500/5 border-red-500/20":"bg-amber-500/5 border-amber-500/20"}`}>
              <W className={`w-4 h-4 mt-0.5 flex-shrink-0 ${w.severity==="high"?"text-red-400":"text-amber-400"}`}/>
              <p className={`text-xs leading-relaxed ${w.severity==="high"?"text-red-300":"text-amber-300"}`}>{w.message}</p></div>);})}</div>
        )}

        <div className="px-5 mb-4">{MC(primary_match,"Primary Match",Award,"text-[#e91e63]",true)}</div>
        <div className="px-5 mb-4">{MC(secondary_match,"Secondary Match",Target,"text-[#2196f3]",false)}</div>

        {/* Zones */}
        {mode==="multipoint" && zones?.length>0 && (
          <div className="px-5 mb-4 space-y-3">
            <div className="flex items-center gap-2"><Layers className="w-4 h-4 text-[#e91e63]"/><span className="text-xs font-medium uppercase tracking-widest text-[#e91e63]">Zone Analysis</span></div>
            {zones.map((z,i)=>{const zs=CS[z.primary_match.confidence]||CS.Approximate;const zc={"Cervical (Neck)":"#FF8C42","Middle (Body)":"#22FF66","Incisal (Tip)":"#00D4FF"};return(
              <Card key={i} data-testid={`zone-card-${i}`} className="rounded-2xl bg-[#232134] border-[#33304a]"><CardContent className="p-4"><div className="flex items-center gap-3">
                <div className="w-2 h-10 rounded-full" style={{backgroundColor:zc[z.zone]||"#888"}}/>
                <div className="flex-1"><span className="text-xs font-medium text-[#7a7894]">{z.zone}</span>
                  <div className="flex items-center gap-3 mt-1"><div className="w-10 h-10 rounded-lg border border-white/10 flex items-center justify-center" style={{backgroundColor:SHADE_COLORS[z.primary_match.shade]||"#ccc"}}>
                    <span className="text-sm font-bold text-[#1a182a]">{z.primary_match.shade}</span></div>
                    <span className="text-lg font-bold text-white">{z.primary_match.shade}</span><span className="text-xs text-[#5a5873]">DE {z.primary_match.delta_e}</span></div></div>
                <Badge className={`text-xs rounded-full px-2.5 ${zs.bg} ${zs.color} border ${zs.border}`}>{z.primary_match.confidence}</Badge>
              </div></CardContent></Card>);})}
          </div>
        )}

        {/* Top 5 */}
        <div className="px-5 mb-4">
          <DropdownMenu><DropdownMenuTrigger asChild>
            <Button data-testid="all-matches-dropdown-btn" variant="outline" className="w-full h-11 rounded-xl bg-[#232134] border-[#33304a] text-[#7a7894] hover:bg-[#2a273b] hover:text-white">
              <BarChart3 className="w-4 h-4 mr-2"/>View Top 5 Matches<ChevronDown className="w-4 h-4 ml-auto"/></Button></DropdownMenuTrigger>
            <DropdownMenuContent className="w-72 bg-[#1a182a] border-[#33304a]">{all_matches?.map((m,i)=>{const s=CS[m.confidence]||CS.Approximate;return(
              <DropdownMenuItem key={m.shade} data-testid={`match-row-${m.shade}`} className="flex items-center gap-3 py-2.5 px-3 text-white focus:bg-[#2a273b] focus:text-white">
                <span className="text-xs font-mono text-[#3d3b55] w-4">{i+1}</span>
                <div className="w-7 h-7 rounded-md border border-white/10" style={{backgroundColor:SHADE_COLORS[m.shade]||"#ccc"}}/>
                <span className="font-semibold flex-1">{m.shade}</span><span className={`text-xs ${s.color}`}>{m.confidence}</span>
                <span className="text-xs text-[#5a5873] font-mono">{m.delta_e}</span></DropdownMenuItem>);})}</DropdownMenuContent></DropdownMenu>
        </div>

        {/* Calibration Data - Hidden by default */}
        <div className="px-5 mb-4">
          <button data-testid="toggle-cal-data-btn" onClick={() => setShowCalData(!showCalData)}
            className="w-full flex items-center justify-between p-3 rounded-xl bg-[#232134] border border-[#33304a] text-[#7a7894] hover:text-white transition-colors">
            <div className="flex items-center gap-2"><Info className="w-3.5 h-3.5"/><span className="text-xs font-medium uppercase tracking-widest">Calibration Data</span></div>
            {showCalData ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
          </button>
          {showCalData && (
            <div className="mt-2 grid grid-cols-2 gap-3 animate-fade-in">
              <div className="space-y-1 p-3 rounded-xl bg-[#15132a]">
                <p className="text-[10px] uppercase tracking-wider text-[#3d3b55]">Corrected L*a*b*</p>
                <p className="text-sm font-mono text-white">{corrected_tooth_lab?.map(v=>v.toFixed(1)).join(", ")}</p></div>
              <div className="space-y-1 p-3 rounded-xl bg-[#15132a]">
                <p className="text-[10px] uppercase tracking-wider text-[#3d3b55]">Adaptation Offset</p>
                <p className="text-sm font-mono text-white">{calibration_applied?.map(v=>(v>=0?"+":"")+v.toFixed(1)).join(", ")}</p></div>
            </div>
          )}
        </div>

        {/* Visual Verification */}
        {image && (
          <div className="px-5 mb-4"><Card data-testid="visual-verification-card" className="rounded-2xl bg-[#232134] border-[#33304a] overflow-hidden"><CardContent className="p-0">
            <div className="px-4 pt-4 pb-2 flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#e91e63]"/><span className="text-xs font-medium uppercase tracking-widest text-[#5a5873]">Visual Verification</span></div>
            <img src={image} alt="Original" className="w-full max-h-[30vh] object-contain" data-testid="verification-image"/></CardContent></Card></div>
        )}

        {/* Actions */}
        <div className="px-5 space-y-3">
          <Button data-testid="export-lab-slip-btn" onClick={() => setLabSlipOpen(true)} variant="outline"
            className="w-full h-12 rounded-2xl bg-[#232134] border-[#33304a] text-white hover:bg-[#2a273b] hover:border-[#e91e63]/30 transition-all">
            <FileText className="w-5 h-5 mr-2 text-[#e91e63]"/>Export Digital Lab Slip
          </Button>
          <Button data-testid="start-new-btn" onClick={handleStartNew}
            className="btn-gradient w-full h-14 text-base font-semibold rounded-2xl text-white border-0 shadow-lg shadow-[#e91e63]/20">
            <RefreshCw className="w-5 h-5 mr-2"/>Start New Match
          </Button>
        </div>

        {/* Lab Slip Dialog - with email & name required for lead capture */}
        <Dialog open={labSlipOpen} onOpenChange={setLabSlipOpen}>
          <DialogContent className="bg-[#1a182a] border-[#33304a] text-white max-w-md">
            <DialogHeader><DialogTitle className="text-white">Digital Lab Slip</DialogTitle>
              <DialogDescription className="text-[#5a5873]">Enter your details to export the PDF report.</DialogDescription></DialogHeader>
            <div className="space-y-4 py-2">
              {[
                {key:"email",label:"Email *",placeholder:"your@clinic.com",required:true},
                {key:"name",label:"Your Name *",placeholder:"Dr. Smith",required:true},
                {key:"patient_id",label:"Patient ID",placeholder:"Optional"},
                {key:"clinician_name",label:"Clinician Name",placeholder:"If different from above"},
                {key:"clinic_name",label:"Clinic Name",placeholder:"Optional"},
                {key:"notes",label:"Notes for Lab",placeholder:"Additional instructions..."},
              ].map(f => (
                <div key={f.key} className="space-y-2">
                  <Label className="text-[#7a7894]">{f.label}</Label>
                  <Input data-testid={`lab-slip-${f.key.replace(/_/g,'-')}`} value={labForm[f.key]}
                    onChange={(e) => setLabForm({...labForm,[f.key]:e.target.value})}
                    placeholder={f.placeholder} className="bg-[#15132a] border-[#33304a] text-white"/>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button data-testid="lab-slip-download-btn" onClick={handleExportPDF} disabled={exporting}
                className="btn-gradient w-full h-12 rounded-xl text-white border-0">
                {exporting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Generating...</> : <><Download className="w-4 h-4 mr-2"/>Download PDF</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
